import { createContext, useContext, useId, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface FieldControl {
  id: string
  invalid: boolean
  describedBy?: string
}

const FieldControlContext = createContext<FieldControl | null>(null)

export function useFieldControl(): FieldControl | null {
  return useContext(FieldControlContext)
}

interface PortalFieldProps {
  label: string
  required?: boolean
  hint?: string
  error?: string
  className?: string
  fullWidth?: boolean
  children: ReactNode
}

export function PortalField({
  label,
  required,
  hint,
  error,
  className,
  fullWidth,
  children,
}: PortalFieldProps) {
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined
  const control: FieldControl = { id, invalid: Boolean(error), describedBy }

  return (
    <FieldControlContext.Provider value={control}>
      <div className={cn('prod-field', fullWidth && 'sm:col-span-2', className)}>
        <label htmlFor={id} className={cn('prod-field__label', required && 'prod-field__label--required')}>
          {label}
        </label>
        {children}
        {hint ? (
          <p id={hintId} className="prod-field__hint">
            {hint}
          </p>
        ) : null}
        {error ? (
          <p id={errorId} className="prod-error">
            {error}
          </p>
        ) : null}
      </div>
    </FieldControlContext.Provider>
  )
}
